import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { listAdminOrders } from '@/services/orders';
import { listAdminLicenses } from '@/services/licenses';

interface AdminStats {
  products: number;
  plans: number;
  orders: number;
  licenses: number;
  customers: number;
  activeSites: number;
  revenue: number;
}

async function fetchStats(): Promise<AdminStats> {
  if (!supabase) throw new Error('Supabase not configured');

  const [products, plans, orders, licenses, customers, activeSites, paidOrders] = await Promise.all([
    supabase.from('products').select('id', { count: 'exact', head: true }),
    supabase.from('plans').select('id', { count: 'exact', head: true }).eq('active', true),
    supabase.from('orders').select('id', { count: 'exact', head: true }),
    supabase.from('licenses').select('id', { count: 'exact', head: true }),
    supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'customer'),
    supabase.from('license_activations').select('id', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('orders').select('amount').eq('status', 'paid'),
  ]);

  const revenue = (paidOrders.data ?? []).reduce((sum: number, o: { amount: number }) => sum + Number(o.amount), 0);

  return {
    products: products.count ?? 0,
    plans:    plans.count ?? 0,
    orders:   orders.count ?? 0,
    licenses: licenses.count ?? 0,
    customers: customers.count ?? 0,
    activeSites: activeSites.count ?? 0,
    revenue,
  };
}

export default function AdminOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentOrders, setRecentOrders] = useState<Awaited<ReturnType<typeof listAdminOrders>>>([]);
  const [recentLicenses, setRecentLicenses] = useState<Awaited<ReturnType<typeof listAdminLicenses>>>([]);
  const [loading, setLoading] = useState(isSupabaseConfigured);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    let alive = true;

    Promise.all([fetchStats(), listAdminOrders(), listAdminLicenses()])
      .then(([s, orders, licenses]) => {
        if (!alive) return;
        setStats(s);
        setRecentOrders(orders.slice(0, 5));
        setRecentLicenses(licenses.slice(0, 5));
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, []);

  const statCards = [
    { label: 'Products', value: stats?.products ?? '—', icon: 'ri-box-3-line', color: 'bg-brand-50 text-brand-600', path: '/admin/products' },
    { label: 'Active Plans', value: stats?.plans ?? '—', icon: 'ri-price-tag-3-line', color: 'bg-indigo-50 text-indigo-600', path: '/admin/plans' },
    { label: 'Orders', value: stats?.orders ?? '—', icon: 'ri-shopping-bag-3-line', color: 'bg-sky-50 text-sky-600', path: '/admin/orders' },
    { label: 'Licenses', value: stats?.licenses ?? '—', icon: 'ri-key-2-line', color: 'bg-violet-50 text-violet-600', path: '/admin/licenses' },
    { label: 'Customers', value: stats?.customers ?? '—', icon: 'ri-team-line', color: 'bg-amber-50 text-amber-600', path: '/admin/customers' },
    { label: 'Active Sites', value: stats?.activeSites ?? '—', icon: 'ri-global-line', color: 'bg-green-50 text-green-600', path: '/admin/activations' },
    { label: 'Revenue', value: stats ? `$${stats.revenue.toLocaleString()}` : '—', icon: 'ri-money-cny-circle-line', color: 'bg-rose-50 text-rose-600', path: '/admin/orders' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Admin Overview</h1>
        <p className="text-sm text-surface-500 mt-1">Key metrics and recent activity across your store.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {statCards.map((s) => (
          <div key={s.label} onClick={() => navigate(s.path)} className="bg-white rounded-xl border border-surface-200 p-5 hover:shadow-md transition-all cursor-pointer">
            <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
              <i className={`${s.icon} text-lg`} />
            </div>
            <p className={`text-2xl font-bold text-surface-900 ${loading ? 'animate-pulse' : ''}`}>{s.value}</p>
            <p className="text-xs text-surface-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
            <h2 className="font-semibold text-surface-900 text-sm">Recent Orders</h2>
            <button onClick={() => navigate('/admin/orders')} className="text-xs font-medium text-brand-600 hover:text-brand-700">View All</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Ref</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Customer</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Amount</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="px-5 py-6 text-center text-surface-500 text-xs">Loading...</td></tr>}
                {!loading && recentOrders.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-center text-surface-500 text-xs">No orders yet.</td></tr>}
                {recentOrders.map((order) => (
                  <tr key={order.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                    <td className="px-5 py-3 font-semibold text-surface-900">{order.orderRef}</td>
                    <td className="px-5 py-3 text-surface-600">{order.userId}</td>
                    <td className="px-5 py-3 font-semibold text-surface-900">${order.amount}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'paid' ? 'bg-green-50 text-green-700' : order.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
            <h2 className="font-semibold text-surface-900 text-sm">Recent Licenses</h2>
            <button onClick={() => navigate('/admin/licenses')} className="text-xs font-medium text-brand-600 hover:text-brand-700">View All</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Product</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Key</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Sites</th>
                  <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="px-5 py-6 text-center text-surface-500 text-xs">Loading...</td></tr>}
                {!loading && recentLicenses.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-center text-surface-500 text-xs">No licenses yet.</td></tr>}
                {recentLicenses.map((license) => (
                  <tr key={license.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                    <td className="px-5 py-3 font-semibold text-surface-900">{license.productName}</td>
                    <td className="px-5 py-3 font-mono text-surface-500 text-xs">****{license.licenseKeyLast4}</td>
                    <td className="px-5 py-3 text-surface-600">{license.activatedSites}/{license.siteLimit === 999 ? '∞' : license.siteLimit}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${license.status === 'active' ? 'bg-green-50 text-green-700' : license.status === 'expired' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                        {license.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
